import React from 'react';
import { ArrowUpward as ArrowUpwardIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Avatar, Box, Button, Stack, Typography, useTheme } from '@mui/material';

import type { ComingSoonHeroProps } from './ComingSoonHero.types';

/**
 * ComingSoonHero Component
 *
 * A centered content block for "coming soon" feature pages.
 * Displays an optional icon, title, description, and call-to-action buttons.
 * Inspired by the chat EmptyState component and the "coming soon" page designs.
 *
 * @example
 * ```tsx
 * <ComingSoonHero
 *   icon={<ChatIcon />}
 *   title="Patient Communications"
 *   description="Coming soon"
 *   primaryAction={{
 *     label: "I'm Interested",
 *     href: "https://jotpsych.com/contact",
 *     external: true,
 *   }}
 * />
 * ```
 */
export const ComingSoonHero: React.FC<ComingSoonHeroProps> = ({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  useAvatar = false,
}) => {
  const theme = useTheme();

  const renderActionButton = (
    action: ComingSoonHeroProps['primaryAction'],
    isSecondary = false,
  ) => {
    if (!action) return null;

    const {
      label,
      href,
      onClick,
      variant = isSecondary ? 'outlined' : 'contained',
      external = false,
      startIcon,
    } = action;

    const buttonProps = {
      variant,
      onClick,
      startIcon:
        startIcon !== undefined ? (
          startIcon === null ? undefined : (
            startIcon
          )
        ) : !isSecondary ? (
          <ArrowUpwardIcon />
        ) : undefined,
      endIcon: external ? <OpenInNewIcon /> : undefined,
      sx: {
        textTransform: 'none',
        fontWeight: 600,
        px: 2.5,
        borderRadius: 2,
      },
    };

    if (href) {
      return (
        <Button
          component="a"
          href={href}
          target={external ? '_blank' : undefined}
          rel={external ? 'noopener noreferrer' : undefined}
          {...buttonProps}
        >
          {label}
        </Button>
      );
    }

    return <Button {...buttonProps}>{label}</Button>;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 6,
        px: 2,
        minHeight: 400,
      }}
    >
      {/* Icon */}
      {icon && (
        <Box sx={{ mb: 3 }}>
          {useAvatar ? (
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'primary.light',
                color: 'white',
                '& svg': { fontSize: 32 },
              }}
            >
              {icon}
            </Avatar>
          ) : (
            <Box
              sx={{
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '& svg': { fontSize: 64 },
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      )}

      {/* Title */}
      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          color: theme.palette.text.primary,
          mb: description ? 2 : 4,
        }}
      >
        {title}
      </Typography>

      {/* Description */}
      {description && (
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            mb: primaryAction || secondaryAction ? 4 : 0,
            maxWidth: 600,
            lineHeight: 1.6,
            ...(typeof description !== 'string' && {
              '& > *': { mb: 1 },
              '& > *:last-child': { mb: 0 },
            }),
          }}
        >
          {description}
        </Typography>
      )}

      {/* Action Buttons */}
      {(primaryAction || secondaryAction) && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          {primaryAction && renderActionButton(primaryAction)}
          {secondaryAction && renderActionButton(secondaryAction, true)}
        </Stack>
      )}
    </Box>
  );
};
